import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Badge, Box, HStack, IconButton, Tag, Text, VStack } from '@chakra-ui/react';
import { FiTrash2 } from 'react-icons/fi'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { formatSlotTime, timeToMinutes } from './lobbyScheduleUtils'
import { CHIP_OVERFLOW_PILL_PX, CHIP_STRIP_GAP_PX, fitChipCount } from './timelineLayout'
import type { CardLayout } from './timelineLayout'
import type { ScheduleAssignmentDto, ScheduleLegislatorRowDto, ScheduleMeetingDto } from '../../../types/lobbyDay'
import { StatusChip } from '../../shared'

/** A single assignment chip — a nested draggable pulled to another meeting (move) or the pool
 *  (unassign). Stops pointerdown/click from bubbling so grabbing a chip never drags the card. */
function AssignmentChip({ meetingId, assignment, highlighted, conflicted, onHoverAttendee }: {
  meetingId: string
  assignment: ScheduleAssignmentDto
  highlighted: boolean
  conflicted: boolean
  onHoverAttendee?: (attendeeId: string | null) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `assignment:${assignment.assignmentId}`,
    data: { type: 'assignment', attendeeId: assignment.attendeeId, assignmentId: assignment.assignmentId, fromMeetingId: meetingId },
  })
  // Attendance styling sits BELOW conflict in precedence: a genuinely conflicted chip must stay
  // styled as a conflict (conflicts are a scheduling concern, distinct from no-show tracking),
  // so an absent-but-conflicted chip is never treated as "absent" here.
  const isAbsent = !conflicted && assignment.attendance === 'Absent'
  // Late counts as present (DB-5) but is flagged in warning styling, below conflict and no-show.
  const isLate = !conflicted && assignment.attendance === 'Late'
  const scheme = conflicted ? 'red' : isAbsent ? 'red' : isLate ? 'gray'
    : assignment.constituency === 'Constituent' ? 'green' : 'gray'
  const attendanceAttr = assignment.attendance === 'Absent' ? 'absent'
    : assignment.attendance === 'Late' ? 'late' : 'present'
  return (
    <Tag.Root
      ref={setNodeRef} {...attributes} {...listeners}
      size="sm" colorPalette={scheme} variant={conflicted || isAbsent || highlighted ? 'solid' : 'subtle'}
      // Late takes the warning status tokens (StatusChip's warning idiom), not a raw palette.
      {...(isLate && !highlighted ? { bg: 'warning.subtle', color: 'warning.fg' } : {})}
      data-attendance={attendanceAttr}
      textDecoration={isAbsent ? 'line-through' : undefined}
      cursor="grab" opacity={isDragging ? 0.4 : 1}
      // Compose: run dnd's own pointerdown (starts the chip drag) THEN stop the event so it
      // never reaches the card body's drag listener. Overriding onPointerDown outright would
      // disable chip dragging, since {...listeners} already sets it.
      onPointerDown={(e) => { listeners?.onPointerDown?.(e); e.stopPropagation() }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => onHoverAttendee?.(assignment.attendeeId)}
      onMouseLeave={() => onHoverAttendee?.(null)}
    >
      <Tag.Label>{assignment.fullName}</Tag.Label>
    </Tag.Root>
  );
}

/** A single non-wrapping row of attendee chips that collapses its overflow into a "+N" pill,
 *  so a duration-locked card never slices a chip in half. Measures the rendered chip widths
 *  (via ResizeObserver + a layout pass that renders all chips first) and trims to what fits;
 *  the "+N" pill bubbles its click up to open the meeting's detail sheet (full roster there). */
function AttendeeChipStrip({ meetingId, assignments, highlightAttendeeIds, conflictedAttendeeIds, onHoverAttendee }: {
  meetingId: string
  assignments: ScheduleAssignmentDto[]
  highlightAttendeeIds?: Set<string> | null
  conflictedAttendeeIds?: Set<string>
  onHoverAttendee?: (attendeeId: string | null) => void
}) {
  const rowRef = useRef<HTMLDivElement>(null)
  // null = "measuring" (render every chip so their widths can be read); a number = trimmed count.
  const [visible, setVisible] = useState<number | null>(null)

  useLayoutEffect(() => { setVisible(null) }, [assignments])

  useLayoutEffect(() => {
    if (visible !== null) return // only measure while all chips are rendered
    const row = rowRef.current
    if (!row) return
    const widths = Array.from(row.querySelectorAll<HTMLElement>('[data-chip]')).map((c) => c.offsetWidth)
    setVisible(fitChipCount(widths, row.clientWidth, CHIP_OVERFLOW_PILL_PX, CHIP_STRIP_GAP_PX))
  }, [visible, assignments])

  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const ro = new ResizeObserver(() => setVisible(null)) // width changed → re-measure from scratch
    ro.observe(row)
    return () => ro.disconnect()
  }, [])

  if (assignments.length === 0) return null
  const shown = visible ?? assignments.length
  const hidden = assignments.length - shown

  return (
    <HStack ref={rowRef} gap={`${CHIP_STRIP_GAP_PX}px`} mt={1} overflow="hidden" align="center">
      {assignments.slice(0, shown).map((a) => (
        <Box key={a.assignmentId} data-chip flexShrink={0}>
          <AssignmentChip
            meetingId={meetingId} assignment={a}
            highlighted={!!highlightAttendeeIds?.has(a.attendeeId)}
            conflicted={!!conflictedAttendeeIds?.has(a.attendeeId)}
            onHoverAttendee={onHoverAttendee}
          />
        </Box>
      ))}
      {hidden > 0 && (
        <Tag.Root size="sm" colorPalette="gray" variant="subtle" flexShrink={0} onPointerDown={(e) => e.stopPropagation()}>
          <Tag.Label>+{hidden}</Tag.Label>
        </Tag.Root>
      )}
    </HStack>
  );
}

interface MeetingAgendaCardProps {
  meeting: ScheduleMeetingDto
  legislator: ScheduleLegislatorRowDto | undefined
  color: string
  layout: CardLayout
  isConflicted: boolean
  isFlashing?: boolean
  isHighlighted?: boolean
  conflictedAttendeeIds?: Set<string>
  highlightAttendeeIds?: Set<string> | null
  top: number
  height: number
  left: string
  width: string
  onOpen: (meetingId: string) => void
  onDelete: (meetingId: string) => void
  onResize: (meetingId: string, durationMin: number) => void
  onHoverAttendee?: (attendeeId: string | null) => void
  onHoverCard?: (legislatorId: string | null) => void
}

export function MeetingAgendaCard({
  meeting, legislator, color, layout, isConflicted, isFlashing, isHighlighted,
  conflictedAttendeeIds, highlightAttendeeIds, top, height, left, width,
  onOpen, onDelete, onResize, onHoverAttendee, onHoverCard,
}: MeetingAgendaCardProps) {
  const drop = useDroppable({ id: `meeting:${meeting.id}`, data: { type: 'meeting', meetingId: meeting.id } })
  const drag = useDraggable({ id: `card:${meeting.id}`, data: { type: 'meeting-card', meetingId: meeting.id } })
  const setBodyRef = (el: HTMLElement | null) => { drop.setNodeRef(el); drag.setNodeRef(el) }

  const baseDuration = timeToMinutes(meeting.endTime) - timeToMinutes(meeting.startTime)
  const [previewDuration, setPreviewDuration] = useState<number | null>(null)
  const resizeStart = useRef<{ y: number; base: number } | null>(null)

  const onResizePointerDown = (e: React.PointerEvent) => {
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    resizeStart.current = { y: e.clientY, base: baseDuration }
    setPreviewDuration(baseDuration)
  }
  const onResizePointerMove = (e: React.PointerEvent) => {
    if (!resizeStart.current) return
    const steps = Math.round((e.clientY - resizeStart.current.y) / 20)
    setPreviewDuration(Math.max(15, Math.min(180, resizeStart.current.base + steps * 15)))
  }
  const onResizePointerUp = (e: React.PointerEvent) => {
    if (!resizeStart.current) return
    const final = previewDuration ?? baseDuration
    resizeStart.current = null
    setPreviewDuration(null)
    if (final !== baseDuration) onResize(meeting.id, final)
    e.stopPropagation()
  }

  const conflictedCount = meeting.assignments.filter((a) => conflictedAttendeeIds?.has(a.attendeeId)).length
  const noShowCount = meeting.assignments.filter((a) => a.attendance === 'Absent').length
  const lateCount = meeting.assignments.filter((a) => a.attendance === 'Late').length
  const tier = layout.mode === 'vertical' ? layout.tier : null
  const showFull = tier === 'full'
  const showCount = tier === 'condensed'

  const nameEl = (
    <HStack gap={1} align="center" minW={0} w="full">
      <Text fontWeight="bold" fontSize="sm" lineClamp={1} minW={0} flex="1">{legislator?.fullName ?? 'Unknown legislator'}</Text>
      {meeting.status === 'Tentative' && (
        <Box as="span" title="Tentative — not yet confirmed" flexShrink={0}
          w="6px" h="6px" borderRadius="full" borderWidth="1px" borderColor="gray.400" />
      )}
      {meeting.status === 'Cancelled' && (
        <Box as="span" title="Cancelled" flexShrink={0}
          fontSize="2xs" color="red.500" textTransform="uppercase">canc</Box>
      )}
      {/* Sits in nameEl, so like the Cancelled marker it shows in every density tier. */}
      {meeting.status === 'DropOff' && (
        <Box as="span" title="Drop-off" flexShrink={0}
          fontSize="2xs" color="warning.fg" fontWeight="semibold">Drop-off</Box>
      )}
    </HStack>
  )
  const timeEl = (
    <Text fontSize="xs" color="gray.500" lineClamp={1}>
      {formatSlotTime(meeting.startTime)}–{formatSlotTime(meeting.endTime)}
      {` · ${previewDuration ?? baseDuration}m`}
    </Text>
  )
  const chipsEl = (
    <AttendeeChipStrip
      meetingId={meeting.id} assignments={meeting.assignments}
      highlightAttendeeIds={highlightAttendeeIds} conflictedAttendeeIds={conflictedAttendeeIds}
      onHoverAttendee={onHoverAttendee}
    />
  )
  // Room and ask share ONE line (gray room · blue ask) — collapsing them frees the vertical
  // room a short card needs so the attendee strip below still fits instead of being clipped.
  // LW-3: when a meeting has NO ask, render an explicit "⚠ No ask" warning in the ask's slot
  // instead of nothing — a no-ask meeting is almost always a mistake, and always rendering the
  // meta line keeps the attendee chip strip from sliding up into where the ask should be (which
  // read as if the first attendee were the ask).
  const askEl = meeting.askTitle
    ? <Box as="span" color="info.fg">{meeting.askTitle}</Box>
    : <Box as="span" color="warning.fg" fontWeight="semibold">⚠ No ask</Box>
  const metaEl = (
    <Text fontSize="xs" lineClamp={1}>
      {meeting.room && <Box as="span" color="gray.400">{meeting.room}</Box>}
      {meeting.room && <Box as="span" color="gray.300"> · </Box>}
      {askEl}
    </Text>
  )
  const countBadge = conflictedCount > 0
    ? <Badge colorPalette="red" mt={1} fontSize="2xs">⚠ {conflictedCount} clash</Badge>
    : meeting.assignments.length > 0
      ? <Badge colorPalette="gray" mt={1} fontSize="2xs">👤 {meeting.assignments.length}</Badge>
      : null
  // At-a-glance no-show flag — a compact danger badge next to countBadge, kept visible in the
  // condensed tier (alongside the neutral count badge) and in every tier that shows chips, so a
  // no-show never requires opening the meeting to notice.
  const noShowBadge = noShowCount > 0
    ? <Badge colorPalette="red" mt={1} fontSize="2xs">{noShowCount} no-show{noShowCount === 1 ? '' : 's'}</Badge>
    : null
  const lateBadge = lateCount > 0
    ? <Box mt={1}><StatusChip variant="warning">{lateCount} late</StatusChip></Box>
    : null

  const borderColor = drop.isOver ? 'accent'
    : isConflicted ? 'red.300'
      : isHighlighted ? 'purple.300' : 'gray.200'
  const shadow = isFlashing ? '0 0 0 3px var(--chakra-colors-blue-400)'
    : isHighlighted ? '0 0 0 2px var(--chakra-colors-purple-300)' : 'sm'

  return (
    <Box
      ref={setBodyRef} {...drag.listeners} {...drag.attributes} role="group"
      data-conflict={isConflicted ? 'true' : 'false'}
      style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left, width }}
      bg="surface" borderWidth="1px" borderColor={borderColor}
      borderLeftWidth="4px" borderLeftColor={`${color}.400`} borderRadius="md" p={1.5}
      shadow={shadow} transition="box-shadow 0.15s ease" overflow="hidden"
      cursor="pointer" onClick={() => onOpen(meeting.id)}
      onMouseEnter={() => onHoverCard?.(legislator?.legislatorId ?? null)}
      onMouseLeave={() => onHoverCard?.(null)}
    >
      <IconButton
        aria-label="Delete meeting"
        size="xs"
        variant="ghost"
        colorPalette="red"
        position="absolute"
        top="2px"
        right="2px"
        zIndex={1}
        opacity={0}
        _groupHover={{ opacity: 1 }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(meeting.id) }}><FiTrash2 /></IconButton>

      {layout.mode === 'split' ? (
        <HStack align="flex-start" gap={2} h="full">
          <VStack align="stretch" gap={0} flex="0 0 45%" minW={0}>
            {nameEl}{timeEl}
          </VStack>
          <VStack align="stretch" gap={0.5} flex="1" minW={0} pr={5}>
            {metaEl}
            {chipsEl}
            {(noShowBadge || lateBadge) && <HStack gap={1}>{noShowBadge}{lateBadge}</HStack>}
          </VStack>
        </HStack>
      ) : (
        <>
          <Box pr={5}>{nameEl}</Box>
          {timeEl}
          {showFull && metaEl}
          {showFull && chipsEl}
          {showFull && (noShowBadge || lateBadge) && <HStack gap={1}>{noShowBadge}{lateBadge}</HStack>}
          {showCount && (
            <HStack gap={1}>
              {countBadge}
              {noShowBadge}
              {lateBadge}
            </HStack>
          )}
        </>
      )}

      <Box
        aria-label="Resize duration" position="absolute" bottom="1px" left="50%" transform="translateX(-50%)"
        w="26px" h="6px" borderRadius="3px" bg="gray.300" cursor="ns-resize" zIndex={1}
        onPointerDown={onResizePointerDown} onPointerMove={onResizePointerMove} onPointerUp={onResizePointerUp}
        onClick={(e) => e.stopPropagation()}
      />
    </Box>
  );
}
